// SmartSelect.jsx (JS)
import React from "react";
import { Select } from "antd";

export default function SmartSelect({
  options = [], // [{ value, label }]
  value, // รองรับทั้ง primitive หรือ { value, label } (จะ normalize ให้)
  onChange,
  placeholder = "ทั้งหมด",
  maxRender = 100,
  debounceMs = 200,
  allowClear = true,
  style,
  className,
  returnObject = false, // <-- ถ้าอยากให้ onChange ส่ง {value,label} ให้เปิด true
  ...rest
}) {
  const [q, setQ] = React.useState("");
  const baseRef = React.useRef(options);
  const [filtered, setFiltered] = React.useState(options.slice(0, maxRender));
  const [open, setOpen] = React.useState(false);

  // normalize helper
  const getLabel = (o) => String(o?.label ?? o?.value ?? "");
  const norm = (s) => String(s ?? "").toLowerCase();

  // อัปเดตแหล่งข้อมูลเมื่อ options เปลี่ยน
  React.useEffect(() => {
    baseRef.current = options;
    // รีคำนวณด้วย query เดิม + keep selected option
    setFiltered((prev) => {
      const src = options || [];
      const out = src
        .filter((o) => (!q ? true : norm(getLabel(o)).includes(norm(q))))
        .slice(0, maxRender);

      // ensure current selected option is present
      const selVal = typeof value === "object" ? value?.value : value;
      if (selVal !== undefined && selVal !== null) {
        const hasSel = out.some((o) => o?.value === selVal);
        if (!hasSel) {
          const found = src.find((o) => o?.value === selVal);
          if (found) out.unshift(found);
        }
      }
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // debounce filter
  React.useEffect(() => {
    const t = setTimeout(() => {
      const src = baseRef.current || [];
      const out = src
        .filter((o) => (!q ? true : norm(getLabel(o)).includes(norm(q))))
        .slice(0, maxRender);

      // keep selected option แม้ไม่ตรง q
      const selVal = typeof value === "object" ? value?.value : value;
      if (selVal !== undefined && selVal !== null) {
        const hasSel = out.some((o) => o?.value === selVal);
        if (!hasSel) {
          const found = src.find((o) => o?.value === selVal);
          if (found) out.unshift(found);
        }
      }

      setFiltered(out);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [q, debounceMs, maxRender, value]);

  // เตรียม value ที่ส่งเข้า antd Select
  // - ถ้า returnObject=false: ส่ง primitive (ค่า .value)
  // - ถ้า returnObject=true: ใช้โหมด labelInValue และส่ง {value, label}
  const normalizeToPrimitive = (v) =>
    typeof v === "object" && v !== null ? v.value : v;

  const primitiveValue = normalizeToPrimitive(value);
  const objectValue =
    typeof value === "object" && value !== null
      ? value
      : (() => {
          const found = (baseRef.current || []).find(
            (o) => o.value === primitiveValue
          );
          return found ? { value: found.value, label: found.label } : undefined;
        })();

  const handleChange = (val, option) => {
    if (returnObject) {
      // ส่ง { value, label }
      if (val === undefined) onChange?.(undefined);
      else
        onChange?.({
          value: val?.value ?? val,
          label: option?.label ?? option?.labelInValue ?? option?.label,
        });
    } else {
      // ส่ง primitive
      onChange?.(typeof val === "object" && val !== null ? val.value : val);
    }
  };

  const handleClear = () => {
    setQ("");
    onChange?.(undefined);
  };

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={setQ}
      options={filtered}
      // โหมดค่า
      labelInValue={returnObject}
      value={returnObject ? objectValue : primitiveValue}
      onChange={handleChange}
      allowClear={allowClear}
      onClear={handleClear}
      placeholder={placeholder}
      virtual
      listHeight={256}
      style={style}
      className={className}
      onDropdownVisibleChange={(v) => {
        setOpen(v);
        if (!v) setQ(""); // ปิดแล้วล้าง query
      }}
      open={open}
      {...rest}
    />
  );
}
